import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Shell } from '../componentes/Shell.jsx';
import { guardarConConexion } from '../nucleo/api/cliente.js';
import {
  ListadoActualizando, ListadoCargando, ListadoConError,
} from '../nucleo/listados/EstadosDeListado.jsx';
import { ESTADO_DE_LISTADO, useConsultaDeListado } from '../nucleo/listados/useConsultaDeListado.js';
import AvisoDeError from '../nucleo/avisos/AvisoDeError.jsx';
import ConfirmacionGeneral from '../nucleo/componentes/ConfirmacionGeneral.jsx';
import {
  consultarCategorias,
  consultarUsoDeCategoria,
  desactivarCategoria,
  reactivarCategoria,
  reordenarCategorias,
} from '../nucleo/api/nucleoApi.js';
import '../estilos/categorias.css';

/**
 * Categorías (RF-007 a RF-010).
 *
 * LISTA PLANA por tipo: no hay categoría padre ni subcategorías (D-04). Catálogo
 * compartido y de EDICIÓN ABIERTA: cualquiera de las dos personas crea, renombra,
 * recolorea, reordena, desactiva y reactiva cualquier categoría, incluidas las
 * sembradas (D-05, RN-035). Aquí nunca aparece NO_ES_PROPIETARIO (RN-036).
 *
 * Antes de desactivar se consulta el uso y se muestra el desglose (RN-037).
 * Reactivar es inmediato y sin confirmación (RN-011, RN-041).
 *
 * ---------------------------------------------------------------------------
 * QUÉ CAMBIÓ EN EL INCREMENTO 10 (ciclo de revisión 2026-10) Y QUÉ NO
 *
 * H-06 · El renglón pasa a DOS LÍNEAS: identidad arriba, las cuatro acciones
 * abajo (RF-165, D-275, D-276), cada renglón en su propia tarjeta con 10 px de
 * aire (D-282). Las medidas y el porqué están en `estilos/categorias.css`.
 *
 * H-09 · La confirmación ya NO se arma como una tarjeta del flujo del documento:
 * la presenta el componente único del núcleo (RF-166, D-277), con posición fija,
 * cortina y foco dentro. Y el acuse —y el error, si falla— se presentan ANCLADOS
 * EN EL SITIO DEL RENGLÓN y no en una franja al principio de la pantalla
 * (RN-477, RN-505): ése era el problema simétrico de H-09, el acuse aparecía
 * donde la persona ya no estaba mirando.
 *
 * LO QUE NO SE TOCÓ, Y ES A PROPÓSITO (RN-473): el enganche del botón, los dos
 * pasos de la baja, la consulta de uso y el refresco del listado. Las cuatro
 * cosas funcionaban; tocarlas sería arreglar algo que no está roto.
 *
 * D-289 · La tarjeta de una categoría desactivada lleva además el MARCO del color
 * de su leyenda (RF-167). Es presentación y vive entero en la hoja de estilo: aquí
 * solo se marca el estado del renglón.
 * ---------------------------------------------------------------------------
 */

/*
 * TEXTOS NORMATIVOS DE §6.6 DEL ANÁLISIS (D-283 a D-286, RN-510 a RN-518).
 *
 * Van exportados y en un solo sitio para que se afirmen carácter por carácter
 * (RNF-096) y para que nadie los reescriba a mano en dos lugares, que es
 * exactamente el hallazgo D-284: la consecuencia de desactivar vivía duplicada en
 * el frontend y en el servidor con redacción divergente.
 *
 * Comillas TIPOGRÁFICAS en el título y en el acuse (RN-518).
 */
export const TITULO_DE_LA_BAJA = (nombre) => `¿Desactivar “${nombre}”?`;

export const ACUSE_DE_LA_BAJA = (nombre) => `Categoría “${nombre}” desactivada. Sigue apareciendo en el historial.`;

export const ACUSE_DE_LA_REACTIVACION = (nombre) => `Categoría “${nombre}” reactivada.`;

/**
 * La consecuencia de desactivar, en sus tres variantes. El selector es N = 0,
 * N = 1 o N > 1.
 *
 * La variante de UN SOLO REGISTRO es un CAMBIO DE TEXTO declarado (D-285), no una
 * transcripción: hasta hoy la pantalla decía «La usan 1 registros del hogar… esos
 * registros se conservan… ya no podrán elegirla», cuatro concordancias falladas en
 * una frase y en el caso más probable de todos —una categoría que se acaba de usar
 * una vez—. Se corrigió ÚNICAMENTE la concordancia: ni una palabra más.
 *
 * La variante sin uso es la UNIFICADA (D-284): conserva lo que decía el diálogo
 * («Todavía no la usa ningún registro») y lo que aportaba el servidor («Podrás
 * reactivarla cuando quieras»), porque RN-334 prohíbe aprovechar un arreglo para
 * quitar información. El «¿Continuar?» se queda SOLO en el mensaje del servidor:
 * ese viaja sin botones, y aquí debajo están «Sí, desactivar» y «Cancelar».
 */
export function consecuenciaDeDesactivar(total) {
  if (total <= 0) {
    return 'Todavía no la usa ningún registro. Si la desactivas dejará de ofrecerse en la captura. Podrás reactivarla cuando quieras.';
  }
  if (total === 1) {
    return 'La usa 1 registro del hogar. Si la desactivas, ese registro se conserva pero ya no podrá elegirla.';
  }
  return `La usan ${total} registros del hogar. Si la desactivas, esos registros se conservan pero ya no podrán elegirla.`;
}

export const ADVERTENCIA_DE_CONTEO_INCOMPLETO = 'No se pudo verificar el uso en algún módulo; el conteo está incompleto.';

export const ROTULO_DE_ACEPTAR_LA_BAJA = 'Sí, desactivar';
export const ROTULO_MIENTRAS_DESACTIVA = 'Desactivando…';

export default function Categorias() {
  const [tipo, setTipo] = useState('GASTO');
  const [mostrarInactivas, setMostrarInactivas] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [porConfirmar, setPorConfirmar] = useState(null);
  const [desactivando, setDesactivando] = useState(false);

  /*
   * CUATRO ESTADOS EXCLUYENTES (RF-087, D-83, RN-140 a RN-142), con la misma
   * implementación única que Cuentas, Tarjetas, Movimientos y la vista del mes.
   *
   * "Aún no tienes categorías" es un RESULTADO, no una etapa: no se pinta antes de
   * tener la respuesta (RN-141), y un fallo de consulta se muestra como error con
   * Reintentar, nunca disfrazado de catálogo vacío.
   *
   * La lista se conserva en un estado propio porque el reordenamiento es
   * optimista: se pinta el orden nuevo antes de que responda el servidor.
   */
  const consultar = useCallback(
    () => consultarCategorias({ tipo, soloActivas: !mostrarInactivas }),
    [tipo, mostrarInactivas],
  );
  const listado = useConsultaDeListado(consultar, (respuesta) => (respuesta ?? []).length === 0);
  const { estado } = listado;
  const cargar = listado.recargar;

  useEffect(() => {
    if (listado.datos) setCategorias(listado.datos);
  }, [listado.datos]);

  /*
   * EL ANCLA: dónde se pinta el acuse o el error de la última acción (RN-477,
   * RN-505). Guarda el renglón sobre el que se actuó —su identificador y su
   * posición— para poder pintar el aviso EN SU SITIO, aunque el renglón ya no esté
   * en la lista porque la baja lo sacó de la vista de activas.
   */
  const [ancla, setAncla] = useState(null);

  /*
   * Ya no hay franja de avisos al principio de la pantalla: el acuse y el error de
   * cada acción viven anclados en el sitio del renglón (RN-477, RN-505). Limpiarla
   * antes de cada acción evita que el aviso de la anterior se lea como el de ésta.
   */
  const limpiarAvisos = () => setAncla(null);

  async function pedirBaja(categoria, indice) {
    limpiarAvisos();
    try {
      const usos = await consultarUsoDeCategoria(categoria.idCategoria);
      setPorConfirmar({ categoria, indice, usos });
    } catch (fallo) {
      // Si un módulo no responde, la confirmación se pide igual advirtiendo que
      // el conteo está incompleto (CONSUMIDOR_NO_DISPONIBLE).
      setPorConfirmar({ categoria, indice, usos: [], incompleto: true });
    }
  }

  /*
   * La pantalla ejecuta la operación; el componente de confirmación NO la ejecuta
   * y NO se cierra solo (§7 bis). Por eso aquí hay un estado "en curso": entre el
   * toque y la respuesta la pregunta sigue abierta y el botón de aceptar deja de
   * admitir un segundo toque (RN-499).
   */
  async function confirmarBaja() {
    if (desactivando) return;
    const { categoria, indice } = porConfirmar;
    setDesactivando(true);
    try {
      await guardarConConexion(() => desactivarCategoria(categoria.idCategoria, { confirmado: true, version: categoria.version }));
      setPorConfirmar(null);
      setAncla({ idCategoria: categoria.idCategoria, indice, exito: ACUSE_DE_LA_BAJA(categoria.nombre) });
      await cargar();
    } catch (fallo) {
      // RN-505: la pregunta se cierra, el error se presenta con el aviso único del
      // 08 en el sitio del renglón y la categoría SIGUE activa.
      setPorConfirmar(null);
      setAncla({ idCategoria: categoria.idCategoria, indice, error: fallo });
    } finally {
      setDesactivando(false);
    }
  }

  async function reactivar(categoria, indice) {
    limpiarAvisos();
    try {
      await guardarConConexion(() => reactivarCategoria(categoria.idCategoria, categoria.version));
      setAncla({ idCategoria: categoria.idCategoria, indice, exito: ACUSE_DE_LA_REACTIVACION(categoria.nombre) });
      await cargar();
    } catch (fallo) {
      setAncla({ idCategoria: categoria.idCategoria, indice, error: fallo });
    }
  }

  async function mover(indice, direccion) {
    const destino = indice + direccion;
    if (destino < 0 || destino >= categorias.length) return;

    limpiarAvisos();
    const reordenadas = [...categorias];
    [reordenadas[indice], reordenadas[destino]] = [reordenadas[destino], reordenadas[indice]];
    setCategorias(reordenadas);

    try {
      await guardarConConexion(() => reordenarCategorias(reordenadas.map((c) => c.idCategoria)));
    } catch (fallo) {
      setAncla({ idCategoria: categorias[indice].idCategoria, indice, error: fallo });
      await cargar();
    }
  }

  const totalDeUsos = (usos) => usos.filter((u) => u.cantidadRegistros > 0).reduce((suma, u) => suma + u.cantidadRegistros, 0);

  /* El desglose por módulo, ya resuelto en castellano: el componente lo pinta, no
     lo calcula (§7 bis). */
  const desgloseDeUso = (usos) => usos.map((uso) => ({
    clave: uso.modulo,
    texto: uso.cantidadPorUsuario?.length > 0
      ? `${uso.modulo} (${uso.cantidadPorUsuario.map((c) => `${c.cantidad} de ${c.nombre}`).join(', ')})`
      : uso.modulo,
    dato: uso.cantidadRegistros < 0 ? 'no se pudo verificar el uso' : uso.cantidadRegistros,
  }));

  const visibles = estado === ESTADO_DE_LISTADO.CARGANDO || estado === ESTADO_DE_LISTADO.ERROR
    ? []
    : categorias;

  /*
   * Dónde va el ancla. Primero se busca el renglón por su identificador; si ya no
   * está —la baja lo sacó de la vista de solo activas— se usa la posición que
   * ocupaba, que es el sitio donde la persona tenía el dedo. Si era el último, el
   * aviso queda al final de la lista.
   */
  let posicionDelAncla = -1;
  if (ancla) {
    const encontrado = visibles.findIndex((c) => c.idCategoria === ancla.idCategoria);
    posicionDelAncla = encontrado >= 0 ? encontrado : Math.min(ancla.indice, visibles.length);
  }

  const anclaPintada = ancla && (
    ancla.error
      ? (
        <AvisoDeError
          key="ancla"
          error={ancla.error}
          margen="0 0 10px"
          alCerrar={() => setAncla(null)}
          datoDePrueba="aviso-de-error-de-la-accion"
        />
      )
      : (
        <div key="ancla" className="aviso n ancla-de-la-accion" role="status" data-prueba="acuse-de-la-accion" data-estado="exito">
          <button
            type="button"
            className="ancla-de-la-accion__cerrar"
            onClick={() => setAncla(null)}
            aria-label="Cerrar el aviso"
          >
            ✕
          </button>
          ✔ {ancla.exito}
        </div>
      )
  );

  return (
    <Shell titulo="Categorías" volverA="/ajustes">
      <div className="card">
        {/*
          * Los tres filtros llevan punto de agarre estable (RNF-097, RN-520): para
          * comparar el marco contra la leyenda hay que llegar primero al listado con
          * inactivas a la vista, y agarrarse del RÓTULO no sirve —el del tercer botón
          * cambia según el estado, que es justo lo que la prueba está moviendo—. El
          * `data-estado` dice qué se está viendo, no qué hará el botón.
          */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className={`btn ${tipo === 'GASTO' ? '' : 'sec'}`}
            onClick={() => setTipo('GASTO')}
            data-prueba="filtro-de-tipo"
            data-tipo="GASTO"
            data-activo={tipo === 'GASTO'}
          >
            Gasto
          </button>
          <button
            className={`btn ${tipo === 'INGRESO' ? '' : 'sec'}`}
            onClick={() => setTipo('INGRESO')}
            data-prueba="filtro-de-tipo"
            data-tipo="INGRESO"
            data-activo={tipo === 'INGRESO'}
          >
            Ingreso
          </button>
          <button
            className="btn sec"
            onClick={() => setMostrarInactivas(!mostrarInactivas)}
            data-prueba="alternar-inactivas"
            data-estado={mostrarInactivas ? 'inactivas-a-la-vista' : 'solo-activas'}
          >
            {mostrarInactivas ? 'Ver solo activas' : 'Ver también inactivas'}
          </button>
        </div>
      </div>

      <ListadoActualizando visible={listado.actualizando} />

      {estado === ESTADO_DE_LISTADO.CARGANDO && (
        <ListadoCargando renglones={4} texto="Cargando el catálogo…" />
      )}

      {estado === ESTADO_DE_LISTADO.ERROR && (
        <ListadoConError error={listado.error} alReintentar={cargar} />
      )}

      {estado === ESTADO_DE_LISTADO.VACIO && (
        <div className="aviso w">
          Aún no tienes categorías activas de este tipo. Crea una para continuar.
        </div>
      )}

      <div className={`lista-de-categorias${listado.actualizando ? ' lista-de-categorias--actualizando' : ''}`}>
        {visibles.flatMap((categoria, indice) => {
          const renglon = (
            /*
             * D-289 / RF-167 · El estado va en la propia tarjeta, con un
             * distintivo que la prueba puede agarrar sin atarse a un píxel
             * (RN-520): la clase de estado Y el marco resuelto igual al color de
             * su leyenda.
             */
            <div
              key={categoria.idCategoria}
              className={`renglon-de-categoria${categoria.activo ? '' : ' renglon-de-categoria--desactivada'}`}
              data-prueba="renglon-de-categoria"
              data-estado={categoria.activo ? 'activa' : 'desactivada'}
              data-categoria={categoria.nombre}
            >
              {/* LÍNEA 1 · IDENTIDAD. Ningún control vive aquí (D-276, RN-488). */}
              <div className="renglon-de-categoria__identidad">
                {/* El color guardado se usa como INDICADOR; el texto toma su color del
                    token del tema, nunca al revés (RN-045g). */}
                <div className="punto" style={{ background: categoria.color ?? 'var(--sec)' }} aria-hidden="true">
                  {categoria.nombre.slice(0, 2).toUpperCase()}
                </div>

                <div className="renglon-de-categoria__texto">
                  <b data-prueba="nombre-de-la-categoria">{categoria.nombre}</b>
                  <small>
                    Orden {categoria.orden}
                    {categoria.esSistema && ' · del catálogo inicial'}
                    {/* RN-457a y RN-521d · La palabra NO cede nunca y el marco no la
                        releva: la señal es doble, marco y palabra. */}
                    {!categoria.activo && (
                      <>
                        {' · '}
                        <span className="renglon-de-categoria__leyenda" data-prueba="leyenda-desactivada">desactivada</span>
                      </>
                    )}
                  </small>
                </div>
              </div>

              {/* LÍNEA 2 · LAS CUATRO ACCIONES, en el orden de hoy (RN-492). */}
              <div className="renglon-de-categoria__acciones">
                <button
                  type="button"
                  className="renglon-de-categoria__icono"
                  onClick={() => mover(indice, -1)}
                  disabled={indice === 0}
                  aria-label={`Subir ${categoria.nombre}`}
                  title="Subir"
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="renglon-de-categoria__icono"
                  onClick={() => mover(indice, 1)}
                  disabled={indice === visibles.length - 1}
                  aria-label={`Bajar ${categoria.nombre}`}
                  title="Bajar"
                >
                  ↓
                </button>
                <Link
                  className="renglon-de-categoria__accion"
                  to={`/ajustes/categorias/${categoria.idCategoria}`}
                  data-prueba="accion-editar"
                >
                  Editar
                </Link>
                {categoria.activo
                  ? (
                    <button
                      type="button"
                      className="renglon-de-categoria__accion renglon-de-categoria__accion--destructiva"
                      onClick={() => pedirBaja(categoria, indice)}
                      data-prueba="accion-desactivar"
                    >
                      Desactivar
                    </button>
                  )
                  : (
                    <button
                      type="button"
                      className="renglon-de-categoria__accion renglon-de-categoria__accion--reactivar"
                      onClick={() => reactivar(categoria, indice)}
                      data-prueba="accion-reactivar"
                    >
                      Reactivar
                    </button>
                  )}
              </div>
            </div>
          );

          return indice === posicionDelAncla ? [anclaPintada, renglon] : [renglon];
        })}

        {/* El renglón sobre el que se actuó era el último y ya no está: el aviso
            queda donde estaba, al final de la lista. */}
        {ancla && posicionDelAncla >= visibles.length && anclaPintada}
      </div>

      {/*
        * H-09 RESUELTO · La pregunta la presenta el componente único del núcleo, con
        * posición fija, cortina y foco dentro (RF-166, D-277, RN-496, RN-497). Antes
        * era una tarjeta más del flujo del documento, dibujada después de toda la
        * lista: con el catálogo completo nacía a más de 900 px de donde la persona
        * estaba mirando, y el síntoma era «el botón no hace nada».
        *
        * Los textos los pone esta pantalla, ya resueltos en castellano (§7 bis). El
        * desglose de uso y la advertencia de conteo incompleto son los de siempre
        * (RN-478): eso ya estaba bien.
        */}
      <ConfirmacionGeneral
        abierta={porConfirmar !== null}
        pregunta={porConfirmar ? TITULO_DE_LA_BAJA(porConfirmar.categoria.nombre) : ''}
        consecuencia={porConfirmar ? consecuenciaDeDesactivar(totalDeUsos(porConfirmar.usos)) : ''}
        detalle={porConfirmar ? desgloseDeUso(porConfirmar.usos) : null}
        advertencia={porConfirmar?.incompleto ? ADVERTENCIA_DE_CONTEO_INCOMPLETO : null}
        rotuloDeAceptar={ROTULO_DE_ACEPTAR_LA_BAJA}
        rotuloEnCurso={ROTULO_MIENTRAS_DESACTIVA}
        enCurso={desactivando}
        alAceptar={confirmarBaja}
        alCancelar={() => setPorConfirmar(null)}
        datoDePrueba="confirmacion-de-baja-de-categoria"
      />

      <div style={{ margin: '10px 12px' }}>
        <Link className="btn ancho" to="/ajustes/categorias/nueva">Crear categoría</Link>
      </div>

      <div className="info">
        Las categorías son una <b>lista plana</b>: no hay categoría padre ni subcategorías.
        No se eliminan, se desactivan, y una categoría inactiva sigue apareciendo en el historial. Todo cambio queda en bitácora con el nombre de quien lo hizo.
      </div>
    </Shell>
  );
}
